import { useEffect, useRef, useState, type ComponentType } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ChevronDown,
  Sparkles,
  HeartPulse,
  PauseCircle,
  AlertTriangle,
  ShieldHalf,
  LifeBuoy,
  Library,
  ArrowRight,
  Menu,
  Phone,
  X,
} from 'lucide-react'
import { useVisibleTools } from '@/lib/tools'
import { CHAPTERS, useChapterText, type ChapterEntry } from '@/lib/chapters'
import { useLocale } from '@/lib/locale'
import { useUi } from '@/lib/i18n/ui'
import { QATAR_BRAND, QatarMark } from './brand'
import { useQatarCopy } from './copy'
import LanguageToggle from './LanguageToggle'

type IconCmp = ComponentType<{ className?: string }>

const CHAPTER_ICONS: Record<string, IconCmp> = {
  'new-age-of-scams': Sparkles,
  'emotional-hijack': HeartPulse,
  'pause-and-verify': PauseCircle,
  'warning-signs': AlertTriangle,
  'digital-self-defense': ShieldHalf,
  'if-youre-scammed': LifeBuoy,
}

/**
 * Bank-branded header for the Qatar face. Dark, in the brand's deep tone
 * (maroon for Al Khaleeji, purple for the QNB preset), with a thin gold rule
 * and the mark reversed out; the language switch and the fraud line are
 * always one tap away. Dropdown panels stay white for legibility.
 */
export default function QatarHeader() {
  const [openMenu, setOpenMenu] = useState<'course' | 'tools' | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const ref = useRef<HTMLElement>(null)
  const tools = useVisibleTools()
  const c = useQatarCopy()
  const t = useUi()
  const locale = useLocale()

  useEffect(() => {
    if (!openMenu) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenMenu(null)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenMenu(null)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenu])

  // Close menus on a language switch so the dropdown re-renders in place.
  useEffect(() => {
    setOpenMenu(null)
  }, [locale])

  // Articles are reachable from the homepage band, not the menu (Lee, 2026-09-08).
  const resourceItems = [
    { to: '/scam-library', label: c.navLibrary, icon: Library as IconCmp },
    ...tools.map(tool => ({ to: tool.to, label: tool.label, icon: tool.icon })),
  ]

  return (
    <header ref={ref} className="sticky top-0 z-40 border-b border-white/10 bg-[var(--qa-hero)]">
      <div className="h-[3px] w-full bg-gradient-to-r from-[var(--qa-accent)] via-[var(--qa-accent-soft)] to-[var(--qa-accent)]" />
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-3.5">
        <Link to="/" className="shrink-0" aria-label={QATAR_BRAND.name[locale]}>
          <QatarMark onDark className="h-10" />
        </Link>

        <nav className="order-3 hidden w-full items-center justify-center gap-1 lg:order-none lg:flex lg:w-auto lg:justify-end sm:gap-4">
          <NavMenu
            label={c.navCourse}
            isOpen={openMenu === 'course'}
            onToggle={() => setOpenMenu(openMenu === 'course' ? null : 'course')}
            widthClass="w-[19rem]"
          >
            <ul>
              {CHAPTERS.map(entry => (
                <ChapterMenuItem key={entry.basicsSlug} entry={entry} onClick={() => setOpenMenu(null)} />
              ))}
            </ul>
          </NavMenu>
          <NavMenu
            label={c.navTools}
            isOpen={openMenu === 'tools'}
            onToggle={() => setOpenMenu(openMenu === 'tools' ? null : 'tools')}
          >
            <ul>
              {resourceItems.map(item => (
                <li key={item.to}>
                  <MenuLink to={item.to} label={item.label} icon={item.icon} onClick={() => setOpenMenu(null)} />
                </li>
              ))}
            </ul>
          </NavMenu>
          <Link
            to="/about"
            className="cursor-pointer rounded-md px-3 py-2 text-base font-semibold text-white/85 transition hover:text-white"
          >
            {c.navAbout}
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <a
            href={`tel:${QATAR_BRAND.fraudLine.tel}`}
            className="hidden items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10 md:inline-flex"
          >
            <Phone className="h-4 w-4" />
            {c.navReport}
          </a>
          <LanguageToggle onDark />
          <button
            type="button"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? t.closeMenu : t.openMenu}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-white transition hover:bg-white/10 lg:hidden"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/10 bg-[var(--qa-hero)] lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            <p className="px-1 pt-2 text-sm font-semibold text-[var(--qa-accent-soft)]">{c.menuCourse}</p>
            {CHAPTERS.map(entry => (
              <MobileChapterLink key={entry.basicsSlug} entry={entry} onClick={() => setMobileOpen(false)} />
            ))}
            <div className="my-2 border-t border-white/10" />
            <p className="px-1 pt-2 text-sm font-semibold text-[var(--qa-accent-soft)]">{c.menuResources}</p>
            {resourceItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition hover:bg-white/5"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition hover:bg-white/5"
            >
              {c.navAbout}
            </Link>
            <a
              href={`tel:${QATAR_BRAND.fraudLine.tel}`}
              className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-3 text-base font-semibold text-[var(--qa-accent-soft)]"
            >
              <Phone className="h-4 w-4" />
              {c.navReport}: {QATAR_BRAND.fraudLine.display}
            </a>
            <Link
              to="/basics/$basicsSlug"
              params={{ basicsSlug: 'new-age-of-scams' }}
              onClick={() => setMobileOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--qa-accent)] px-4 py-3.5 text-base font-semibold text-[var(--qa-footer)]"
            >
              {c.startCourse}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}

function ChapterMenuItem({ entry, onClick }: { entry: ChapterEntry; onClick: () => void }) {
  const text = useChapterText(entry)
  const Icon = CHAPTER_ICONS[entry.basicsSlug] ?? Sparkles
  return (
    <li>
      <MenuLink to={`/basics/${entry.basicsSlug}`} label={text.title} icon={Icon} onClick={onClick} />
    </li>
  )
}

function MobileChapterLink({ entry, onClick }: { entry: ChapterEntry; onClick: () => void }) {
  const text = useChapterText(entry)
  return (
    <Link
      to="/basics/$basicsSlug"
      params={{ basicsSlug: entry.basicsSlug }}
      onClick={onClick}
      className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition hover:bg-white/5"
    >
      {text.title}
    </Link>
  )
}

function MenuLink({
  to,
  label,
  icon: Icon,
  onClick,
}: {
  to: string
  label: string
  icon: IconCmp
  onClick: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="group flex items-center gap-3.5 rounded-xl px-3 py-2 text-base font-medium text-slate-700 transition hover:bg-[var(--qa-tint-soft)] hover:text-slate-900"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[var(--qa-primary)]">
        <Icon className="h-5 w-5" />
      </span>
      <span className="flex-1 leading-tight">{label}</span>
    </Link>
  )
}

function NavMenu({
  label,
  isOpen,
  onToggle,
  children,
  widthClass = 'w-[22rem]',
}: {
  label: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
  widthClass?: string
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-2 text-base font-semibold text-white/85 transition hover:text-white"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div
          className={`absolute start-1/2 z-50 mt-3 rounded-2xl bg-white p-2 text-slate-700 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)] ring-1 ring-slate-200/70 ltr:-translate-x-1/2 rtl:translate-x-1/2 ${widthClass}`}
        >
          {children}
        </div>
      )}
    </div>
  )
}
